/* eslint-env browser */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Switch,
  MenuItem,
  Button,
  ButtonBase,
  Chip,
  InputAdornment,
  IconButton,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  GlobalStyles,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import GpsFixedRoundedIcon from '@mui/icons-material/GpsFixedRounded';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import TrackChangesRoundedIcon from '@mui/icons-material/TrackChangesRounded';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SearchOffRoundedIcon from '@mui/icons-material/SearchOffRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { api } from 'common';
import CircularLoading from '../components/CircularLoading';
import { showToast, confirmDialog, InlineMessage, EmptyState } from '../components/feedback';
import { tokens as c, iconHues, layout } from '../theme';
import { formatAddisDateTime, ADDIS_OFFSET_MS } from '../common/exportUtils';
import { BookingImageSettings, DeliveryFlow, CustomerBidSettings } from 'common/sharedFunctions';
import { showEst, calcEst, optionsRequired } from 'common/sharedFunctions';
import { onPageStateReset } from '../components/modern/pageState';

// Settings > General (L4 / W2_Portal_Settings): seven sections in a list with
// counts, one search across every setting, a short label and a helper line
// for each, errors under the field they belong to, the gift end in Addis
// 12-hour time, and a save bar that counts unsaved changes.
//
// The form still saves the WHOLE /settings object it loaded (editSettings
// merges it and nulls the keys this tab removed), with the same launch
// stamps and number clean-up as before. Every setting the old form had is
// here; the onboarding wizard keys live on their own tab and ride along.

// Same text as the server's default (functions sharedSMS, holiday_promo).
const HOLIDAY_PROMO_DEFAULT_MESSAGE = 'እንኳን ለ2019 አዲስ አመት በሰላም አደረሳችሁ!\nከDonkey Drive የወርሃዊ ነጻ ክሬዲት ፓኬጅ ስጦታ ተበርክቶሎታል! አዲሱ ዓመት የሰላም፣ የፍቅር፣ የጤና እና የትልቅ ስኬት ይሁንልዎት! መልካም በአል !\nመረጃ ለማግኘት፡  9260';

const SECTION_STORE_KEY = 'dd_settings_general_section';
const SCROLL_MARGIN = { scrollMarginTop: `${layout.APP_BAR_HEIGHT + 24}px`, scrollMarginBottom: '128px' };
// While this tab is open, anything the browser scrolls into view (a focused
// field, a jump to an error) stays clear of the app bar and the save bar,
// also above a phone keyboard.
const PAGE_SCROLL_PADDING = { html: { scrollPaddingTop: `${layout.APP_BAR_HEIGHT + 16}px`, scrollPaddingBottom: '128px' } };

// An unsaved draft survives switching Settings tabs (the tab panel unmounts
// this view) for the rest of the browser session, as long as the settings it
// started from are still the ones in the store; when another tab saved in
// between, the draft's own edits are replayed on the new settings. The last
// saved copy is kept the same way (a save now also updates the store -
// common editSettings - so the next tab never starts from old values).
let draftCache = null; // { source, base, data }
let savedCache = null; // { source, data }
// A draft or saved copy belongs to the person signed in: gone at sign-out.
onPageStateReset(() => { draftCache = null; savedCache = null; });

const SECTIONS = [
  { id: 'currency', Icon: PaymentsOutlinedIcon, hue: 'accent', title: ['settings_sec_currency', 'Currency & payments'], sub: ['settings_sec_currency_sub', 'Prices, payment methods, tips and the wallet.'] },
  { id: 'login', Icon: VpnKeyOutlinedIcon, hue: 'info', title: ['settings_sec_login', 'Login & security'], sub: ['settings_sec_login_sub', 'How people sign in, and the safety checks.'] },
  { id: 'drivers', Icon: GpsFixedRoundedIcon, hue: 'success', title: ['settings_sec_drivers', 'Drivers & dispatch'], sub: ['settings_sec_drivers_sub', 'Approval, documents and how requests reach drivers.'] },
  { id: 'bookings', Icon: MapOutlinedIcon, hue: 'info', title: ['settings_sec_bookings', 'Bookings & maps'], sub: ['settings_sec_bookings_sub', 'Country, distances, the booking flow and the Donkey Meter.'] },
  { id: 'demand', Icon: TrackChangesRoundedIcon, hue: 'amber', title: ['settings_sec_demand', 'Demand & city events'], sub: ['settings_sec_demand_sub', 'Hot zones for drivers and crowd events in the city.'] },
  { id: 'portal', Icon: DesktopWindowsOutlinedIcon, hue: 'neutral', title: ['settings_sec_portal', 'Portal & notifications'], sub: ['settings_sec_portal_sub', 'Staff portal options and bulk notifications.'] },
  {
    id: 'flatrate',
    Icon: CardGiftcardOutlinedIcon,
    hue: 'accent',
    title: ['settings_sec_flatrate', 'Flat rate & gifts'],
    sub: ['settings_sec_flatrate_sub', 'Daily plan for drivers and gifts for new sign-ups.'],
    groups: [
      { id: 'flat', title: ['settings_grp_flat_rate', 'Daily flat rate'], jump: ['settings_grp_flat_rate', 'Daily flat rate'] },
      { id: 'gift', title: ['settings_grp_gift', 'New Year gift for new sign-ups'], jump: ['settings_jump_gift', 'New Year gift'] },
    ],
  },
];
const SECTION_IDS = SECTIONS.map((s) => s.id);

// Field parsers: the same rules the old form's handlers applied.
const parseNonNegative = (raw) => (parseFloat(raw) >= 0 ? parseFloat(raw) : '');
const parsePositive = (raw) => (parseFloat(raw) > 0 ? parseFloat(raw) : '');
const parseUnitInterval = (raw) => {
  // 0-1, kept as a number so validate_car_image picks it up (it ignores
  // anything else and falls back to its 0.6 default).
  const v = parseFloat(raw);
  return v >= 0 && v <= 1 ? v : '';
};
const parseWelcomeDays = (raw) => {
  // 0-7 whole days x the daily flat rate; complete_onboarding clamps again.
  const v = parseInt(raw, 10);
  return v >= 0 && v <= 7 ? v : '';
};
const parseFinite = (raw) => {
  // The raw value is kept so a typo is refused at Save, never blanked.
  const v = parseFloat(raw);
  return Number.isFinite(v) ? v : '';
};
const parseWholeNonNegative = (raw) => {
  // withdrawFetchBoundDays: 0 / blank = all time (legacy).
  const v = parseInt(raw, 10);
  return Number.isFinite(v) && v >= 0 ? v : '';
};
// Flat-rate packages: digits and commas only, kept as typed ("1,7,30"); the
// app parses and de-duplicates (common/other/flatRate.js).
const parsePackages = (raw) => String(raw || '').replace(/[^0-9,\s]/g, '');

const SW = 'switch';
const TXT = 'text';
const NUM = 'number';
const SEL = 'select';
const COUNTRY = 'country';
const MULTI = 'multiline';
const ADDIS_TIME = 'addisTime';

const truthy = (key) => (d) => Boolean(d[key]);
const isOn = (key) => (d) => d[key] === true;
const notOff = (key) => (d) => d[key] !== false;
const flatOff = (d) => d.driverFlatRateEnabled !== true;
const giftOff = (d) => d.holidayPromoEnabled !== true;
const currencyOpts = (d) => ({ currency: d.code || d.symbol || 'ETB' });

// Every setting on this tab, in display order. label/help are [key, English].
const SETTINGS = [
  // Currency & payments
  { key: 'symbol', section: 'currency', kind: TXT, label: ['currency_symbol', 'Currency symbol'], help: ['settings_help_symbol', 'Shown with every price.'] },
  { key: 'code', section: 'currency', kind: TXT, label: ['currency_code', 'Currency code'], help: ['settings_help_code', 'The three-letter code, for example ETB.'] },
  { key: 'decimal', section: 'currency', kind: NUM, parse: parseNonNegative, inputProps: { min: 0, step: 1 }, label: ['set_decimal', 'Decimal places'], help: ['settings_help_decimal', 'Digits after the decimal point in prices.'] },
  { key: 'swipe_symbol', section: 'currency', kind: SW, checked: truthy('swipe_symbol'), label: ['swipe_symbol', 'Symbol after the amount'], help: ['settings_help_swipe_symbol', 'On: 100 {{symbol}}. Off: {{symbol}} 100.'], helpOpts: (d) => ({ symbol: d.symbol || 'ETB' }) },
  { key: 'disable_online', section: 'currency', kind: SW, checked: truthy('disable_online'), label: ['disable_online', 'Disable online payments'], help: ['settings_help_disable_online', 'Hides the online payment methods in the apps.'] },
  { key: 'disable_cash', section: 'currency', kind: SW, checked: truthy('disable_cash'), label: ['disable_cash', 'Disable cash payments'], help: ['settings_help_disable_cash', 'Customers can no longer choose cash.'] },
  { key: 'walletMoneyField', section: 'currency', kind: TXT, label: ['wallet_money_field', 'Wallet denominations'], help: ['settings_help_wallet_amounts', 'Top-up amounts offered in the wallet, separated by commas, for example 100,200,500.'] },
  { key: 'tipMoneyField', section: 'currency', kind: TXT, label: ['tip_money_field', 'Tip denominations'], help: ['settings_help_tip_amounts', 'Tip amounts offered after a trip, separated by commas.'] },
  { key: 'disable_tips', section: 'currency', kind: SW, checked: truthy('disable_tips'), label: ['disable_tips', 'Disable tips'], help: ['settings_help_disable_tips', 'Customers are not asked for a tip.'] },
  { key: 'prepaid', section: 'currency', kind: SW, checked: truthy('prepaid'), hidden: () => !(showEst && !optionsRequired), label: ['prepaid', 'Prepaid'] },
  { key: 'disablesystemprice', section: 'currency', kind: SW, checked: truthy('disablesystemprice'), hidden: () => !(calcEst && !optionsRequired), label: ['disablesystemprice', 'Disable system price'] },
  { key: 'RiderWithDraw', section: 'currency', kind: SW, checked: truthy('RiderWithDraw'), label: ['rider_withdraw', 'Customer withdraw'], help: ['settings_help_customer_withdraw', 'Customers can withdraw money from their wallet.'] },
  { key: 'bonus', section: 'currency', kind: NUM, parse: parseNonNegative, inputProps: { min: 0 }, label: ['referral_bonus', 'Referral bonus'], help: ['settings_help_referral_bonus', 'Amount credited for a successful referral.'] },
  { key: 'bank_fields', section: 'currency', kind: SW, checked: truthy('bank_fields'), label: ['bank_fields', 'Bank details at sign-up'], help: ['settings_help_bank_fields', 'Drivers add their bank details when they sign up.'] },

  // Login & security
  { key: 'otp_secure', section: 'login', kind: SW, checked: truthy('otp_secure'), label: ['settings_label3', 'Booking OTP'], help: ['settings_help_booking_otp', 'Customers get a start code, and the driver enters it to start the trip.'] },
  { key: 'emailLogin', section: 'login', kind: SW, checked: truthy('emailLogin'), label: ['email_login', 'Email login'], help: ['settings_help_email_login', 'Sign in with email and password.'] },
  { key: 'mobileLogin', section: 'login', kind: SW, checked: truthy('mobileLogin'), label: ['mobile_login', 'Mobile login'], help: ['settings_help_mobile_login', 'Sign in with a phone number and a code.'] },
  { key: 'socialLogin', section: 'login', kind: SW, checked: truthy('socialLogin'), label: ['social_login', 'Social login'], help: ['settings_help_social_login', 'Sign in with a Google account.'] },
  { key: 'imageIdApproval', section: 'login', kind: SW, checked: truthy('imageIdApproval'), label: ['settings_label6', 'Verify ID or passport'], help: ['settings_help_verify_id', 'Customers add an ID or passport photo before they can book.'] },
  // Default ON: flip OFF to stop writing /staffActivityLogs if RTDB cost
  // spikes. The Security dashboard keeps working on the stored entries.
  { key: 'enableStaffActivityLogging', section: 'login', kind: SW, checked: notOff('enableStaffActivityLogging'), label: ['settings_lbl_staff_logging', 'Staff activity log'], help: ['settings_help_staff_logging', 'Records staff actions for the Security dashboard. Turn it off to cut database cost.'] },
  { key: 'panic', section: 'login', kind: TXT, inputMode: 'tel', label: ['panic_num', 'Panic dial number'], help: ['settings_help_panic', "The number the app's SOS button calls."] },

  // Drivers & dispatch
  { key: 'driver_approval', section: 'drivers', kind: SW, checked: truthy('driver_approval'), label: ['settings_label4', 'Driver approval'], help: ['settings_help_driver_approval', 'New drivers wait for approval before they can work.'] },
  { key: 'carApproval', section: 'drivers', kind: SW, checked: truthy('carApproval'), label: ['settings_label5', 'Car approval'], help: ['settings_help_car_approval', 'New cars wait for approval.'] },
  // AI car-image validation: ON sends each picked car photo to the
  // validate_car_image function (Google Vision), which hard-blocks non-car
  // images. OFF = no Vision call, no cost.
  { key: 'carImageValidation', section: 'drivers', kind: SW, checked: isOn('carImageValidation'), label: ['settings_car_image_validation', 'Car photo check (AI)'], help: ['settings_help_car_image_ai', 'Each car photo is checked with Google Vision, and photos that are not a car are refused.'] },
  { key: 'carImageConfidenceThreshold', section: 'drivers', kind: NUM, parse: parseUnitInterval, value: (d) => d.carImageConfidenceThreshold ?? '', inputProps: { min: 0, max: 1, step: 0.05 }, label: ['settings_car_image_threshold', 'AI confidence'], help: ['settings_help_car_image_threshold', 'From 0 to 1. Empty uses 0.6. Used only while the car photo check is on.'] },
  { key: 'driverThreshold', section: 'drivers', kind: TXT, inputMode: 'decimal', label: ['driver_threshold', 'Driver threshold'], help: ['settings_help_driver_threshold', "With negative balance allowed, trips stop when a driver's balance reaches this amount."] },
  { key: 'driverRadiusMin', section: 'drivers', kind: NUM, parse: parseNonNegative, inputProps: { min: 0, step: 0.1 }, label: ['driverRadiusMin', 'Driver radius min'] },
  { key: 'driverRadius', section: 'drivers', kind: NUM, parse: parseNonNegative, inputProps: { min: 0, step: 0.1 }, label: ['driverRadius', 'Driver radius max'], help: ['settings_help_driver_radius', 'Requests go first to drivers within this distance, in km (miles when miles are on).'] },
  { key: 'minFoundDrivers', section: 'drivers', kind: NUM, parse: parseNonNegative, inputProps: { min: 0, step: 1 }, label: ['minFoundDrivers', 'Min found drivers'], help: ['settings_help_min_found', 'At least this many drivers get each request. Empty uses 5.'] },
  { key: 'autoDispatch', section: 'drivers', kind: SW, checked: truthy('autoDispatch'), label: ['auto_dispatch', 'Auto dispatch'], help: ['settings_help_auto_dispatch', 'The server sends new and scheduled requests to nearby drivers.'] },
  { key: 'negativeBalance', section: 'drivers', kind: SW, checked: truthy('negativeBalance'), label: ['negative_balance', 'Allow driver negative balance'], help: ['settings_help_negative_balance', 'Drivers can keep working below zero, down to the driver threshold.'] },
  { key: 'realtime_drivers', section: 'drivers', kind: SW, checked: truthy('realtime_drivers'), label: ['realtime_drivers', 'Driver live location'] },
  { key: 'carType_required', section: 'drivers', kind: SW, checked: truthy('carType_required'), label: ['carType_required', 'Car is required'], help: ['settings_help_car_required', 'Drivers must add a car when they sign up.'] },
  { key: 'term_required', section: 'drivers', kind: SW, checked: truthy('term_required'), label: ['term_required', 'Terms required'], help: ['settings_help_terms_required', 'New users must accept the terms and privacy policy.'] },
  { key: 'license_image_required', section: 'drivers', kind: SW, checked: truthy('license_image_required'), label: ['license_image_required', 'Licence photo required'], help: ['settings_help_licence_required', 'Drivers must upload a photo of their driving licence.'] },
  { key: 'CarHornRepeat', section: 'drivers', kind: SW, checked: truthy('CarHornRepeat'), label: ['car_horn_repeat', 'Repeat sound on new trip'] },

  // Bookings & maps
  { key: 'country', section: 'bookings', kind: COUNTRY, by: 'label', label: ['settings_lbl_country', 'Country'], help: ['settings_help_country', 'The country the service runs in.'] },
  { key: 'restrictCountry', section: 'bookings', kind: COUNTRY, by: 'code', label: ['country_restriction', 'Autocomplete country restriction'], help: ['settings_help_country_restriction', 'Address search suggests places in this country only.'] },
  { key: 'AllowCountrySelection', section: 'bookings', kind: SW, checked: truthy('AllowCountrySelection'), label: ['allow_multi_country', 'Allow multi-country selection'], help: ['settings_help_multi_country', 'People can pick another country code for their phone number.'] },
  { key: 'convert_to_mile', section: 'bookings', kind: SW, checked: truthy('convert_to_mile'), label: ['convert_to_mile', 'Convert to miles'], help: ['settings_help_miles', 'Distances show in miles instead of kilometres.'] },
  { key: 'useDistanceMatrix', section: 'bookings', kind: SW, checked: truthy('useDistanceMatrix'), label: ['use_distance_matrix', 'Use the Distance Matrix API'], help: ['settings_help_distance_matrix', "Distance and time come from Google's Distance Matrix, which Google bills."] },
  { key: 'showLiveRoute', section: 'bookings', kind: SW, checked: truthy('showLiveRoute'), label: ['show_live_route', 'Show live route'], help: ['settings_help_live_route', 'The trip screen draws the road route.'] },
  // Book later (customer app, owner 30 Oct): a Later button beside Continue
  // on the search screen that asks the pickup time. Default OFF (rarely
  // used). Customers always pick the car on the search screen now, so the
  // old "Two-step booking" and "Horizontal car list" switches are gone.
  { key: 'searchBookLaterEnabled', section: 'bookings', kind: SW, checked: isOn('searchBookLaterEnabled'), label: ['settings_lbl_book_later', 'Book later'], help: ['settings_help_book_later', 'Customers see a Later button next to Continue on the search screen and choose the pickup time.'] },
  { key: 'enableDonkeyMeter', section: 'bookings', kind: SW, checked: truthy('enableDonkeyMeter'), label: ['enable_donkey_meter', 'Donkey Meter'], help: ['settings_help_donkey_meter', 'Drivers can run the meter for street pickups, with more than one customer.'] },
  { key: 'donkeyMeterPerKm', section: 'bookings', kind: SW, checked: isOn('donkeyMeterPerKm'), disabled: (d) => d.enableDonkeyMeter !== true, label: ['donkey_meter_per_km', 'Charge per km'], help: ['settings_help_meter_per_km', 'Charge by distance plus waiting time, the city tariff. Needs the Donkey Meter on.'] },
  { key: 'meterFallbackSpeedKmh', section: 'bookings', kind: NUM, parse: parsePositive, value: (d) => d.meterFallbackSpeedKmh ?? '', inputProps: { min: 1, step: 1 }, suffix: 'km/h', label: ['settings_lbl_meter_speed', 'Meter fallback speed'], help: ['settings_help_meter_speed', 'Used to estimate the distance when GPS drops out.'] },

  // Demand & city events. Hot zones: two default-OFF switches so the feature
  // can be staged and killed without a deploy (1. demandHeatIngestEnabled
  // counts qualifying requests into /demand_cells; 2. driverDemandMapEnabled
  // makes publishDemandHeat write /demand_heat every 10 min; OFF writes an
  // `off` marker every phone obeys within 3 x the client TTL). City events:
  // portal page (phase 1), drivers inside Hot zones (2), the proximity push
  // (3), customers on the home map, and the nightly auto sources (4).
  { key: 'demandHeatIngestEnabled', section: 'demand', kind: SW, checked: isOn('demandHeatIngestEnabled'), label: ['settings_lbl_demand_ingest', 'Count booking requests'], help: ['settings_help_demand_ingest', 'Step 1. Server counters for Hot zones, near-zero cost. Turn this on first.'] },
  { key: 'driverDemandMapEnabled', section: 'demand', kind: SW, checked: isOn('driverDemandMapEnabled'), label: ['settings_lbl_demand_map', 'Hot zones for drivers'], help: ['settings_help_demand_map', 'Step 2. Published every 10 minutes. Off hides it on every phone within 30 minutes.'] },
  { key: 'cityEventsEnabled', section: 'demand', kind: SW, checked: isOn('cityEventsEnabled'), label: ['settings_lbl_city_events', 'City events'], help: ['settings_help_city_events', 'Call-centre agents enter crowd events and supervisors approve them. Drivers see nothing yet.'] },
  { key: 'cityEventsDriverEnabled', section: 'demand', kind: SW, checked: isOn('cityEventsDriverEnabled'), label: ['settings_lbl_city_events_driver', 'City events for drivers'], help: ['settings_help_city_events_driver', 'Published events show inside Hot zones. Needs Hot zones for drivers on.'] },
  { key: 'cityEventsPushEnabled', section: 'demand', kind: SW, checked: isOn('cityEventsPushEnabled'), label: ['settings_lbl_city_events_push', 'City event alerts'], help: ['settings_help_city_events_push', 'Alerts nearby online, idle drivers 10 to 40 minutes before a crowd leaves. At most 2 a day, never from 11 pm to 6 am.'] },
  { key: 'cityEventsRiderEnabled', section: 'demand', kind: SW, checked: isOn('cityEventsRiderEnabled'), label: ['settings_lbl_city_events_customer', 'City events for customers'], help: ['settings_help_city_events_customer', 'Customers see nearby events on the home map and can book a trip to the venue.'] },
  { key: 'cityEventsAutoFetchEnabled', section: 'demand', kind: SW, checked: isOn('cityEventsAutoFetchEnabled'), label: ['settings_lbl_city_events_auto', 'Automatic event sources'], help: ['settings_help_city_events_auto', 'Every night at 2:40 am the Ethiopian calendar and weekly crowd patterns publish themselves; feeds go to review.'] },

  // Portal & notifications. The RTDB cost flags take effect at the next login
  // or page reload (/settings is a one-shot read).
  { key: 'portalLiveTracking', section: 'portal', kind: SW, checked: isOn('portalLiveTracking'), label: ['settings_portal_live_tracking', 'Live tracking in booking details'], help: ['settings_help_live_tracking', 'Shows the driver on a map for active bookings. It loads only when someone opens it.'] },
  { key: 'portalLiveTrackingRoute', section: 'portal', kind: SW, checked: isOn('portalLiveTrackingRoute'), disabled: (d) => d.portalLiveTracking !== true, label: ['settings_portal_live_tracking_route', 'Show route button'], help: ['settings_help_live_tracking_route', 'Each click is one Google Directions request. Needs live tracking on.'] },
  // Who sees the Gross Earnings cards on the Dashboard. Unset = admin only.
  {
    key: 'dashboardGrossEarnings',
    section: 'portal',
    kind: SEL,
    value: (d) => d.dashboardGrossEarnings || 'admin',
    options: [
      ['admin', ['gross_earnings_admin_only', 'Admin only (default)']],
      ['all', ['gross_earnings_everyone', 'Show to every role']],
      ['none', ['gross_earnings_hidden', 'Hide for every role']],
    ],
    label: ['dashboard_gross_earnings', 'Gross earnings on the dashboard'],
    help: ['settings_help_gross_earnings', 'Who sees the gross earnings tiles.'],
  },
  { key: 'useReportRollups', section: 'portal', kind: SW, checked: isOn('useReportRollups'), label: ['settings_lbl_report_rollups', 'Earnings from server rollups'], help: ['settings_help_report_rollups', 'Earnings reports read the server rollups instead of every booking. Reload the page to apply.'] },
  { key: 'useTripdateMsIndex', section: 'portal', kind: SW, checked: isOn('useTripdateMsIndex'), label: ['settings_lbl_tripdate_index', 'Bookings by date index'], help: ['settings_help_tripdate_index', 'Finds bookings by the tripdateMs index. Turn on only after the backfill. Reload the page to apply.'] },
  { key: 'withdrawFetchBoundDays', section: 'portal', kind: NUM, parse: parseWholeNonNegative, value: (d) => d.withdrawFetchBoundDays ?? 0, inputProps: { min: 0, step: 1 }, label: ['settings_lbl_withdraw_days', 'Withdraw history (days)'], help: ['settings_help_withdraw_days', 'Processed withdraws load for this many days. 0 loads all. Pending ones always load.'] },
  // The campaign engine owns every bulk push/SMS send and is why a recipient
  // gets a notification only once. OFF restores the old inline fan-out (no
  // per-recipient ledger) — only to work around a defect in the engine.
  { key: 'campaignEngineEnabled', section: 'portal', kind: SW, checked: notOff('campaignEngineEnabled'), label: ['settings_campaign_engine', 'Exactly-once bulk delivery (recommended)'], help: ['settings_help_campaign_engine', 'Each person gets a bulk notification once, even when a send is retried. Turn it off only to work around a fault.'] },
  { key: 'campaignSmsSkipIfPush', section: 'portal', kind: SW, checked: isOn('campaignSmsSkipIfPush'), label: ['settings_sms_skip_if_push', 'Skip SMS for recipients reached by push'], help: ['settings_help_sms_skip', 'Saves SMS cost. Off texts everyone who matches.'] },
  { key: 'campaignSmsConcurrency', section: 'portal', kind: NUM, value: (d) => d.campaignSmsConcurrency ?? '', inputProps: { min: 1, max: 25 }, label: ['settings_sms_concurrency', 'SMS sent at once (per worker)'], help: ['settings_sms_concurrency_help', 'Blank = 4. Total in flight is this x 3 delivery workers. Raise once you have watched a campaign; too high and the gateway throttles us.'] },
  { key: 'campaignDuplicateWindowMinutes', section: 'portal', kind: NUM, value: (d) => d.campaignDuplicateWindowMinutes ?? '', inputProps: { min: 0, max: 1440 }, label: ['settings_duplicate_window', 'Warn on identical notification (minutes)'], help: ['settings_duplicate_window_help', 'Blank = 10. Sending the same title, body and audience again inside this window asks for confirmation first. 0 disables the warning.'] },

  // Flat rate & gifts: the daily plan, then the New Year gift (a one-time
  // prepaid-day credit at sign-up for accounts created between the save that
  // switches it on (holidayPromoSince) and the end, with the SMS + push; it
  // shares the once-per-phone ledger with the welcome credit).
  { key: 'driverFlatRateEnabled', section: 'flatrate', group: 'flat', kind: SW, checked: isOn('driverFlatRateEnabled'), label: ['driver_flat_rate_enabled', 'Daily flat-rate plan for drivers'], help: ['settings_help_flat_rate', 'Drivers pay one fee a day instead of a commission on each trip.'] },
  { key: 'flatRateSkipInactiveDays', section: 'flatrate', group: 'flat', kind: SW, checked: isOn('flatRateSkipInactiveDays'), disabled: flatOff, label: ['settings_lbl_skip_inactive', 'Skip inactive days'], help: ['settings_help_skip_inactive', 'Charge only the days with a completed trip.'] },
  { key: 'flatRateAllowFleetDrivers', section: 'flatrate', group: 'flat', kind: SW, checked: isOn('flatRateAllowFleetDrivers'), disabled: flatOff, label: ['settings_lbl_allow_fleet', 'Allow fleet drivers'], help: ['settings_help_allow_fleet', 'Fleet commission still applies.'] },
  { key: 'flatRatePackageDays', section: 'flatrate', group: 'flat', kind: TXT, parse: parsePackages, value: (d) => d.flatRatePackageDays ?? '1,7,30', placeholder: '1,7,30', inputMode: 'numeric', disabled: flatOff, label: ['flat_rate_package_days', 'Prepaid packages (days)'], help: ['settings_help_packages', "In display order, for example 1,7,30. Each package's name comes from the language key flat_rate_pkg_<days>."] },
  { key: 'driverWelcomeCreditEnabled', section: 'flatrate', group: 'flat', kind: SW, checked: isOn('driverWelcomeCreditEnabled'), disabled: flatOff, label: ['driver_welcome_credit_enabled', 'Welcome credit for new drivers'], help: ['settings_help_welcome_credit', 'Free days when a new driver finishes the onboarding wizard.'] },
  { key: 'driverWelcomeCreditDays', section: 'flatrate', group: 'flat', kind: NUM, parse: parseWelcomeDays, value: (d) => d.driverWelcomeCreditDays ?? 1, inputProps: { min: 0, max: 7, step: 1 }, disabled: (d) => d.driverFlatRateEnabled !== true || d.driverWelcomeCreditEnabled !== true, label: ['driver_welcome_credit_days', 'Welcome credit days'], help: ['settings_help_welcome_days', '0 to 7 days of the daily flat rate, credited once to the top-up balance.'] },
  { key: 'holidayPromoEnabled', section: 'flatrate', group: 'gift', kind: SW, checked: isOn('holidayPromoEnabled'), label: ['settings_gift_enabled', 'Give a sign-up gift'], help: ['settings_gift_enabled_help', 'New drivers and customers get prepaid-day credit, an SMS and a notification.'] },
  { key: 'holidayPromoAmount', section: 'flatrate', group: 'gift', kind: NUM, parse: parseFinite, value: (d) => d.holidayPromoAmount ?? 10, inputProps: { min: 1, max: 500, step: 1 }, suffix: 'currency', disabled: giftOff, label: ['settings_gift_drivers', 'Gift for drivers'], help: ['settings_gift_drivers_help', 'Credited to the daily-plan top-up. 10 {{currency}} is one day.'], helpOpts: currencyOpts },
  { key: 'holidayPromoCustomerAmount', section: 'flatrate', group: 'gift', kind: NUM, parse: parseFinite, value: (d) => d.holidayPromoCustomerAmount ?? '', inputProps: { min: 1, max: 500, step: 1 }, suffix: 'currency', placeholder: ['settings_gift_same_as_drivers', 'Same as drivers'], disabled: giftOff, label: ['settings_gift_customers', 'Gift for customers'], help: ['settings_gift_customers_help', "Credited to the customer's wallet. Empty gives the driver amount."] },
  { key: 'holidayPromoEndsAt', section: 'flatrate', group: 'gift', kind: ADDIS_TIME, disabled: giftOff, label: ['settings_gift_ends', 'Gift ends'], help: ['settings_gift_ends_help', 'Addis Ababa time. Sign-ups after this get nothing.'] },
  { key: 'holidayPromoMessage', section: 'flatrate', group: 'gift', kind: MULTI, value: (d) => d.holidayPromoMessage ?? HOLIDAY_PROMO_DEFAULT_MESSAGE, disabled: giftOff, label: ['settings_gift_message', 'Message'], help: ['settings_gift_message_help', 'Sent by SMS and as a notification. The first line becomes the notification title.'] },
];

// What a setting shows: compared with the saved copy to count changes.
const displayValue = (s, d) => {
  if (s.value) return s.value(d);
  if (s.kind === SW) return s.checked(d);
  return d[s.key];
};
const sameValue = (a, b) => {
  const norm = (v) => (v === undefined || v === null ? '' : String(v));
  return norm(a) === norm(b);
};

// Problems that block saving: the old form's rules, shown under the field.
const validate = (d, now) => {
  const errors = {};
  // At least one of mobile and email login stays on.
  if (d.mobileLogin !== true && d.emailLogin !== true) {
    errors.mobileLogin = ['mobile_or_email_cant_off', 'Mobile and email login cannot both be off.'];
  }
  if (d.bonus === '') {
    errors.bonus = ['proper_bonus', 'Please enter proper bonus in numbers.'];
  }
  if (d.holidayPromoEnabled === true) {
    const end = Number(d.holidayPromoEndsAt);
    if (!(end > 0)) {
      errors.holidayPromoEndsAt = ['settings_err_gift_end_required', 'Pick the date and time the gift ends.'];
    } else if (end <= now) {
      errors.holidayPromoEndsAt = ['settings_err_gift_end_past', 'This time has already passed. Pick a later one.'];
    }
    const amountBad = (v) => v !== undefined && v !== '' && !(Number(v) > 0 && Number(v) <= 500);
    if (amountBad(d.holidayPromoAmount)) {
      errors.holidayPromoAmount = ['settings_err_gift_amount', 'Enter 1 to 500 {{currency}}. 10 {{currency}} is one day.'];
    }
    if (amountBad(d.holidayPromoCustomerAmount)) {
      errors.holidayPromoCustomerAmount = ['settings_err_gift_customer_amount', 'Enter 1 to 500 {{currency}}, or leave it empty.'];
    }
  }
  return errors;
};

// Addis Ababa wall clock <-> ms (UTC+3, no DST), whatever the browser's zone.
const msToAddisParts = (ms) => {
  const d = new Date(Number(ms) + ADDIS_OFFSET_MS);
  return { y: d.getUTCFullYear(), m: d.getUTCMonth() + 1, d: d.getUTCDate(), h: d.getUTCHours(), mi: d.getUTCMinutes() };
};
const addisPartsToMs = (p) => Date.UTC(p.y, p.m - 1, p.d, p.h || 0, p.mi || 0) - ADDIS_OFFSET_MS;
const validMs = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};
const pad2 = (n) => (n < 10 ? `0${n}` : String(n));
const HOURS12 = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const MINUTES = [0, 15, 30, 45, 59];

// The gift end: date, hour, minute and am/pm, all in Addis Ababa time; it
// emits absolute ms. Picking a date with no time yet gives 11:59 pm.
function AddisEndField({ id, value, onChange, disabled, error, describedBy, t }) {
  const ms = validMs(value);
  const p = ms ? msToAddisParts(ms) : null;
  const pm = p ? p.h >= 12 : true;
  const h12 = p ? (p.h % 12 === 0 ? 12 : p.h % 12) : 11;
  const minute = p ? p.mi : 59;
  const minutes = MINUTES.indexOf(minute) >= 0 ? MINUTES : MINUTES.concat([minute]).sort((a, b) => a - b);
  const emit = (patch) => {
    const next = Object.assign({ h: 23, mi: 59 }, p || {}, patch);
    if (!next.y) return;
    onChange(addisPartsToMs(next));
  };
  const timeDisabled = disabled || !p;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, justifyContent: { sm: 'flex-end' } }}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          value={p ? dayjs(new Date(p.y, p.m - 1, p.d)) : null}
          onChange={(v) => {
            if (v && typeof v.isValid === 'function' && v.isValid()) emit({ y: v.year(), m: v.month() + 1, d: v.date() });
          }}
          format="D MMM YYYY"
          disabled={disabled}
          slotProps={{
            textField: {
              id,
              size: 'small',
              error: Boolean(error),
              sx: { width: 196 },
              inputProps: { 'aria-describedby': describedBy },
            },
          }}
        />
      </LocalizationProvider>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <TextField
          select
          size="small"
          value={h12}
          disabled={timeDisabled}
          onChange={(e) => emit({ h: (Number(e.target.value) % 12) + (pm ? 12 : 0) })}
          inputProps={{ 'aria-label': t('hour', { defaultValue: 'Hour' }) }}
          sx={{ width: 76 }}
        >
          {HOURS12.map((h) => <MenuItem key={h} value={h}>{h}</MenuItem>)}
        </TextField>
        <Typography component="span" aria-hidden="true" sx={{ px: 0.25, fontWeight: 600 }}>:</Typography>
        <TextField
          select
          size="small"
          value={minute}
          disabled={timeDisabled}
          onChange={(e) => emit({ mi: Number(e.target.value) })}
          inputProps={{ 'aria-label': t('minute', { defaultValue: 'Minute' }) }}
          sx={{ width: 76 }}
        >
          {minutes.map((mi) => <MenuItem key={mi} value={mi}>{pad2(mi)}</MenuItem>)}
        </TextField>
        <ToggleButtonGroup
          exclusive
          size="small"
          color="primary"
          value={pm ? 'pm' : 'am'}
          disabled={timeDisabled}
          onChange={(e, v) => { if (v) emit({ h: (h12 % 12) + (v === 'pm' ? 12 : 0) }); }}
          sx={{ ml: 0.5 }}
        >
          <ToggleButton value="am" sx={{ px: 1.5, height: 40 }}>am</ToggleButton>
          <ToggleButton value="pm" sx={{ px: 1.5, height: 40 }}>pm</ToggleButton>
        </ToggleButtonGroup>
      </Box>
    </Box>
  );
}

function CountryField({ id, countries, by, value, onChange, error, describedBy }) {
  const selected = useMemo(
    () => (countries || []).find((ct) => (by === 'code' ? ct.code === value : ct.label === value)) || null,
    [countries, by, value]
  );
  return (
    <Autocomplete
      id={id}
      options={countries || []}
      autoHighlight
      disableClearable
      value={selected}
      getOptionLabel={(option) => (option && option.label) || ''}
      isOptionEqualToValue={(option, v) => option.label === v.label}
      onChange={(e, v) => { if (v) onChange(by === 'code' ? v.code : v.label); }}
      sx={{ width: { xs: '100%', sm: 280 } }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          error={Boolean(error)}
          inputProps={{ ...params.inputProps, autoComplete: 'new-password', 'aria-describedby': describedBy }}
        />
      )}
    />
  );
}

const IconCircle = ({ Icon, hue, size = 28 }) => {
  const tone = iconHues[hue] || iconHues.neutral;
  return (
    <Box
      component="span"
      aria-hidden="true"
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        bgcolor: tone.bg,
        color: tone.fg,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 'none',
        '& svg': { fontSize: Math.round(size * 0.6) },
      }}
    >
      <Icon />
    </Box>
  );
};

const UnsavedDot = ({ sx }) => (
  <Box component="span" aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: c.ACCENT, flex: 'none', ...sx }} />
);

const ErrorLine = ({ id, children }) => (
  <Box
    id={id}
    role="alert"
    sx={{ mt: 0.75, display: 'flex', alignItems: 'flex-start', gap: 0.5, color: c.DANGER_TEXT, fontSize: 13.5, fontWeight: 600, lineHeight: 1.45, overflowWrap: 'anywhere' }}
  >
    <WarningAmberRoundedIcon aria-hidden="true" sx={{ fontSize: 18, flex: 'none', mt: '1px' }} />
    <span>{children}</span>
  </Box>
);

// One setting: label (an orange dot while unsaved) and helper line on the
// left, the control on the right; a long text field sits under the words.
function SettingRow({ inputId, label, help, helpId, extra, changed, error, errorId, control, rowLayout, disabled }) {
  const stacked = rowLayout === 'stack';
  const isSwitch = rowLayout === 'switch';
  return (
    <Box id={`${inputId}-row`} sx={{ py: 1.75, borderBottom: `1px solid ${c.OUTLINE}`, '&:last-child': { borderBottom: 0 }, ...SCROLL_MARGIN }}>
      <Box sx={{ display: 'flex', flexWrap: isSwitch ? 'nowrap' : 'wrap', alignItems: 'flex-start', columnGap: 3, rowGap: 1.25 }}>
        <Box sx={{ flex: stacked ? '1 1 100%' : '1 1 260px', minWidth: 0 }}>
          <Typography
            component="label"
            htmlFor={inputId}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontSize: 15,
              fontWeight: 600,
              lineHeight: 1.45,
              color: disabled ? c.TEXT_3 : c.INK,
              cursor: isSwitch && !disabled ? 'pointer' : 'default',
              overflowWrap: 'anywhere',
            }}
          >
            {changed ? <UnsavedDot /> : null}
            <span>{label}</span>
          </Typography>
          {help ? (
            <Typography id={helpId} sx={{ mt: 0.25, fontSize: 13.5, lineHeight: 1.5, color: c.TEXT_3, overflowWrap: 'anywhere' }}>
              {help}
            </Typography>
          ) : null}
          {extra ? (
            <Typography sx={{ mt: 0.25, fontSize: 13.5, lineHeight: 1.5, color: c.INK_2, overflowWrap: 'anywhere' }}>
              {extra}
            </Typography>
          ) : null}
        </Box>
        <Box
          sx={{
            flex: stacked ? '1 1 100%' : '0 1 auto',
            ml: isSwitch ? 'auto' : 0,
            minWidth: 0,
            maxWidth: '100%',
            width: stacked || isSwitch ? undefined : { xs: '100%', sm: 'auto' },
            display: 'flex',
            flexDirection: 'column',
            alignItems: stacked ? 'stretch' : { xs: isSwitch ? 'flex-end' : 'stretch', sm: 'flex-end' },
          }}
        >
          {control}
          {error ? <ErrorLine id={errorId}>{error}</ErrorLine> : null}
        </Box>
      </Box>
    </Box>
  );
}

const GeneralSettings = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const { editSettings, clearSettingsViewError, countries } = api;
  const settingsdata = useSelector(state => state.settingsdata);
  const dispatch = useDispatch();
  const tx = (pair, opts) => (pair ? t(pair[0], Object.assign({ defaultValue: pair[1] }, opts || {})) : '');

  // The saved copy (base) and the draft being edited (data).
  const source = settingsdata.settings;
  const [initial] = useState(() => {
    if (draftCache && source && draftCache.source === source) return draftCache;
    // Another tab saved since this draft was left (the store follows saves
    // now): keep the draft's own edits on top of the new settings, so it
    // neither loses them nor puts that tab's old values back.
    if (draftCache && source) {
      const edited = { ...source };
      Object.keys(draftCache.data).forEach((k) => {
        if (draftCache.data[k] !== draftCache.base[k]) edited[k] = draftCache.data[k];
      });
      Object.keys(draftCache.base).forEach((k) => {
        if (!Object.prototype.hasOwnProperty.call(draftCache.data, k)) delete edited[k];
      });
      return { source, base: source, data: edited };
    }
    if (savedCache && source && savedCache.source === source) return { source, base: savedCache.data, data: savedCache.data };
    return source ? { source, base: source, data: source } : null;
  });
  const [base, setBase] = useState(initial ? initial.base : null);
  const [data, setData] = useState(initial ? initial.data : null);
  const sourceRef = useRef(initial ? initial.source : null);
  useEffect(() => {
    if (source && source !== sourceRef.current) {
      sourceRef.current = source;
      setBase(source);
      setData(source);
    }
  }, [source]);

  const [section, setSection] = useState(() => {
    try {
      const saved = window.localStorage.getItem(SECTION_STORE_KEY);
      return SECTION_IDS.indexOf(saved) >= 0 ? saved : SECTION_IDS[0];
    } catch (e) {
      return SECTION_IDS[0];
    }
  });
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [clicked, setClicked] = useState(false);
  const [focusKey, setFocusKey] = useState(null);
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const visibleSettings = useMemo(() => SETTINGS.filter((s) => !(s.hidden && s.hidden())), []);

  const changedKeys = useMemo(() => {
    if (!data || !base) return [];
    return visibleSettings.filter((s) => !sameValue(displayValue(s, data), displayValue(s, base))).map((s) => s.key);
  }, [data, base, visibleSettings]);
  const changedCount = changedKeys.length;

  const errors = useMemo(() => (data ? validate(data, Date.now()) : {}), [data]);
  const errorKeys = visibleSettings.filter((s) => errors[s.key]).map((s) => s.key);

  // Keep the draft when the tab unmounts; warn before leaving the page.
  const latestRef = useRef({});
  latestRef.current = { base, data, source: sourceRef.current, changed: changedCount > 0 };
  useEffect(() => () => {
    const latest = latestRef.current;
    draftCache = latest.changed && latest.source ? { source: latest.source, base: latest.base, data: latest.data } : null;
  }, []);
  const hasChanges = changedCount > 0;
  useEffect(() => {
    if (!hasChanges) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasChanges]);

  // Jump to a setting (the first error on Save): open its section, scroll it
  // into view and put the cursor in it.
  useEffect(() => {
    if (!focusKey) return;
    const row = document.getElementById(`gs-${focusKey}-row`);
    if (row && typeof row.scrollIntoView === 'function') row.scrollIntoView({ block: 'center' });
    const input = document.getElementById(`gs-${focusKey}`);
    if (input && typeof input.focus === 'function') {
      try { input.focus({ preventScroll: true }); } catch (e) { input.focus(); }
    }
    setFocusKey(null);
  }, [focusKey]);

  // Phone: keep the chip of the open section in view in the chip row.
  const chipRowRef = useRef(null);
  const dataReady = Boolean(data);
  useEffect(() => {
    const row = chipRowRef.current;
    if (!row) return;
    const chip = row.querySelector('[data-active="true"]');
    if (!chip) return;
    const delta = chip.getBoundingClientRect().left - row.getBoundingClientRect().left;
    row.scrollLeft = Math.max(0, row.scrollLeft + delta - 8);
  }, [section, dataReady]);

  const openSection = (id) => {
    setSection(id);
    setQuery('');
    try { window.localStorage.setItem(SECTION_STORE_KEY, id); } catch (e) { /* private mode: not remembered */ }
  };
  const jumpTo = (key) => {
    const s = SETTINGS.find((x) => x.key === key);
    if (s) openSection(s.section);
    setFocusKey(key);
  };

  const setField = (key, value) => setData((d) => Object.assign({}, d, { [key]: value }));
  // For the variant hooks below (they take the event-style handlers).
  const handleSwitchChange = (e) => setField(e.target.name, e.target.checked);
  const handleBookingFlowChange = (e) => setField('bookingFlow', e.target.value);
  const handleChangeBidPriceType = (e) => setField('coustomerBidPriceType', e.target.value);
  const handleCustomerPrice = (e) => setField('bidprice', parseFloat(e.target.value) >= 0 ? parseFloat(e.target.value) : '');

  // Search: every label and helper line, in the portal language and in
  // English, plus the stored setting name.
  const searchText = useMemo(() => {
    const map = {};
    SETTINGS.forEach((s) => {
      map[s.key] = [
        t(s.label[0], { defaultValue: s.label[1] }),
        s.help ? t(s.help[0], { defaultValue: s.help[1] }) : '',
        s.label[1],
        s.help ? s.help[1] : '',
        s.key,
      ].join(' ').toLowerCase();
    });
    return map;
  }, [t]);
  const q = query.trim().toLowerCase();
  const searching = q.length > 0;
  const matches = useMemo(
    () => (searching ? visibleSettings.filter((s) => searchText[s.key].indexOf(q) >= 0) : []),
    [searching, visibleSettings, searchText, q]
  );
  const countBySection = useMemo(() => {
    const out = {};
    SECTION_IDS.forEach((id) => { out[id] = 0; });
    (searching ? matches : visibleSettings).forEach((s) => { out[s.section] += 1; });
    return out;
  }, [searching, matches, visibleSettings]);
  const changedSections = useMemo(() => {
    const out = {};
    changedKeys.forEach((key) => {
      const s = SETTINGS.find((x) => x.key === key);
      if (s) out[s.section] = true;
    });
    return out;
  }, [changedKeys]);

  const discard = async () => {
    if (!hasChanges || saving) return;
    const ok = await confirmDialog({
      title: changedCount === 1
        ? t('settings_discard_title_one', { defaultValue: 'Discard 1 unsaved change?' })
        : t('settings_discard_title_many', { n: changedCount, defaultValue: 'Discard {{n}} unsaved changes?' }),
      body: t('settings_discard_body', { defaultValue: 'The settings go back to how they were when you last saved.' }),
      confirmLabel: t('settings_discard', { defaultValue: 'Discard' }),
      destructive: true,
    });
    if (ok && aliveRef.current) setData(base);
  };

  const save = async () => {
    if (!data || saving) return;
    if (!data.AllowCriticalEditsAdmin) {
      showToast.error(t('demo_mode', { defaultValue: 'Restricted in Demo App.' }));
      return;
    }
    const now = Date.now();
    const problems = validate(data, now);
    const first = visibleSettings.find((s) => problems[s.key]);
    if (first) {
      jumpTo(first.key);
      showToast.warning(t('settings_fix_first', { defaultValue: 'Fix the marked setting before saving.' }));
      return;
    }
    // "Since" stamps are written at SAVE time, once each, never reset
    // (turning a flag off keeps its stamp). onboardingWizardSince: accounts
    // created before it are "legacy" (gated by the wizard only while their
    // profile is incomplete); the switch lives on the Onboarding wizard tab,
    // which stamps it too, and this copy is a safety net because this form
    // re-sends every key. driverWelcomeCreditSince: the server's "new
    // driver" threshold for the welcome credit (Auth creationTime >= since).
    const payload = Object.assign({}, data);
    if (payload.onboardingWizardEnabled === true && !payload.onboardingWizardSince) {
      payload.onboardingWizardSince = now;
    }
    if (payload.driverWelcomeCreditEnabled === true && !payload.driverWelcomeCreditSince) {
      payload.driverWelcomeCreditSince = now;
    }
    // holidayPromoSince: start of the New Year gift window (accounts created
    // from this moment until holidayPromoEndsAt qualify). Re-stamped on EVERY
    // off -> on transition (a re-run campaign must not inherit last year's
    // start), unlike the wizard stamps.
    const promoTurningOn = payload.holidayPromoEnabled === true && !(base && base.holidayPromoEnabled === true);
    if (payload.holidayPromoEnabled === true && (!payload.holidayPromoSince || promoTurningOn)) {
      payload.holidayPromoSince = now;
    }
    // Campaign tunables: store a number or nothing. An empty string in
    // /settings is a value every server reader then has to defend against,
    // and `null` deletes the key so the documented default applies instead.
    ['campaignSmsConcurrency', 'campaignDuplicateWindowMinutes'].forEach((key) => {
      if (payload[key] === '' || payload[key] === null || payload[key] === undefined) {
        payload[key] = null;
        return;
      }
      const parsed = parseInt(payload[key], 10);
      payload[key] = Number.isFinite(parsed) ? parsed : null;
    });
    if (payload.campaignSmsConcurrency !== null) {
      payload.campaignSmsConcurrency = Math.min(25, Math.max(1, payload.campaignSmsConcurrency));
    }
    if (payload.campaignDuplicateWindowMinutes !== null) {
      payload.campaignDuplicateWindowMinutes = Math.min(1440, Math.max(0, payload.campaignDuplicateWindowMinutes));
    }
    setSaving(true);
    setClicked(true);
    try {
      await dispatch(editSettings(payload));
      savedCache = { source: sourceRef.current, data: payload };
      if (!aliveRef.current) {
        draftCache = null;
        return;
      }
      setBase(payload);
      setData(payload);
      showToast.success(t('settings_updated', { defaultValue: 'Settings updated' }));
    } catch (e) {
      if (aliveRef.current) showToast.error(t('update_failed', { defaultValue: 'Update failed' }));
    } finally {
      if (aliveRef.current) setSaving(false);
    }
  };

  const closeUpdateError = () => {
    setClicked(false);
    dispatch(clearSettingsViewError());
  };

  if (!data) {
    return <CircularLoading inline />;
  }

  const sinceLine = (s) => {
    if (s.key === 'holidayPromoEnabled' && data.holidayPromoSince) {
      const when = formatAddisDateTime(data.holidayPromoSince);
      if (!when) return null;
      return data.holidayPromoEnabled === true
        ? t('settings_gift_on_since', { when, defaultValue: 'On since {{when}}.' })
        : t('settings_gift_last_started', { when, defaultValue: 'Last started {{when}}.' });
    }
    if (s.key === 'driverWelcomeCreditEnabled' && data.driverWelcomeCreditSince) {
      const when = formatAddisDateTime(data.driverWelcomeCreditSince);
      return when ? t('settings_welcome_since', { when, defaultValue: 'First switched on {{when}}.' }) : null;
    }
    return null;
  };

  const renderControl = (s, ids, errorText) => {
    const disabled = Boolean(s.disabled && s.disabled(data));
    const describedBy = [ids.help, errorText ? ids.error : null].filter(Boolean).join(' ') || undefined;
    if (s.kind === SW) {
      return (
        <Switch
          id={ids.input}
          name={s.key}
          checked={s.checked(data)}
          disabled={disabled}
          onChange={(e) => setField(s.key, e.target.checked)}
          inputProps={{ 'aria-describedby': describedBy }}
          sx={{ my: -0.75 }}
        />
      );
    }
    if (s.kind === COUNTRY) {
      return (
        <CountryField
          id={ids.input}
          countries={countries}
          by={s.by}
          value={data[s.key]}
          onChange={(v) => setField(s.key, v)}
          error={errorText}
          describedBy={describedBy}
        />
      );
    }
    if (s.kind === ADDIS_TIME) {
      return (
        <AddisEndField
          id={ids.input}
          value={data[s.key]}
          onChange={(v) => setField(s.key, v)}
          disabled={disabled}
          error={errorText}
          describedBy={describedBy}
          t={t}
        />
      );
    }
    if (s.kind === SEL) {
      return (
        <TextField
          select
          id={ids.input}
          size="small"
          value={displayValue(s, data)}
          disabled={disabled}
          onChange={(e) => setField(s.key, e.target.value)}
          SelectProps={{ native: true }}
          inputProps={{ 'aria-describedby': describedBy }}
          sx={{ width: { xs: '100%', sm: 280 } }}
        >
          {s.options.map(([value, pair]) => (
            <option key={value} value={value}>{tx(pair)}</option>
          ))}
        </TextField>
      );
    }
    const isMulti = s.kind === MULTI;
    const suffix = s.suffix === 'currency' ? (data.code || data.symbol || 'ETB') : s.suffix;
    const value = displayValue(s, data);
    return (
      <TextField
        id={ids.input}
        name={s.key}
        size="small"
        type={s.kind === NUM ? 'number' : 'text'}
        value={value === undefined || value === null ? '' : value}
        disabled={disabled}
        error={Boolean(errorText)}
        placeholder={Array.isArray(s.placeholder) ? tx(s.placeholder) : s.placeholder}
        multiline={isMulti}
        minRows={isMulti ? 4 : undefined}
        fullWidth={isMulti}
        onChange={(e) => setField(s.key, s.parse ? s.parse(e.target.value) : e.target.value)}
        inputProps={Object.assign(
          { 'aria-describedby': describedBy, 'aria-invalid': errorText ? true : undefined },
          s.inputMode ? { inputMode: s.inputMode } : {},
          s.inputProps || {}
        )}
        InputProps={suffix ? { endAdornment: <InputAdornment position="end">{suffix}</InputAdornment> } : undefined}
        sx={isMulti ? undefined : { width: { xs: '100%', sm: 240 } }}
      />
    );
  };

  const renderRow = (s) => {
    const ids = { input: `gs-${s.key}`, help: s.help ? `gs-${s.key}-help` : null, error: `gs-${s.key}-error` };
    const err = errors[s.key];
    const errorText = err ? tx(err, currencyOpts(data)) : null;
    const disabled = Boolean(s.disabled && s.disabled(data));
    const rowLayout = s.kind === SW ? 'switch' : (s.kind === MULTI ? 'stack' : 'field');
    return (
      <SettingRow
        key={s.key}
        inputId={ids.input}
        label={tx(s.label)}
        help={s.help ? tx(s.help, s.helpOpts ? s.helpOpts(data) : undefined) : null}
        helpId={ids.help}
        extra={sinceLine(s)}
        changed={changedKeys.indexOf(s.key) >= 0}
        error={errorText}
        errorId={ids.error}
        control={renderControl(s, ids, errorText)}
        rowLayout={rowLayout}
        disabled={disabled}
      />
    );
  };

  const groupHeading = (id, pair) => (
    <Typography
      key={`h-${id}`}
      id={id}
      component="h3"
      sx={{ pt: 2.25, pb: 0.25, fontSize: 12.5, fontWeight: 600, lineHeight: 1.5, letterSpacing: '0.02em', color: c.TEXT_3, ...SCROLL_MARGIN }}
    >
      {tx(pair)}
    </Typography>
  );

  const activeSection = SECTIONS.find((sec) => sec.id === section) || SECTIONS[0];
  let paneBody;
  if (searching) {
    paneBody = matches.length === 0 ? (
      <EmptyState
        compact
        icon={<SearchOffRoundedIcon />}
        title={t('settings_search_none', { query: query.trim(), defaultValue: 'No settings match “{{query}}”.' })}
        actionLabel={t('settings_search_clear', { defaultValue: 'Clear search' })}
        onAction={() => setQuery('')}
      />
    ) : (
      SECTIONS.map((sec) => {
        const rows = matches.filter((s) => s.section === sec.id);
        if (!rows.length) return null;
        return (
          <Box key={sec.id}>
            {groupHeading(`gs-results-${sec.id}`, sec.title)}
            <Box>{rows.map(renderRow)}</Box>
          </Box>
        );
      })
    );
  } else {
    const rows = visibleSettings.filter((s) => s.section === activeSection.id);
    if (activeSection.groups) {
      paneBody = activeSection.groups.map((g) => (
        <Box key={g.id}>
          {groupHeading(`gs-group-${g.id}`, g.title)}
          <Box>{rows.filter((s) => s.group === g.id).map(renderRow)}</Box>
        </Box>
      ));
    } else {
      paneBody = <Box>{rows.map(renderRow)}</Box>;
    }
  }

  const totalCount = visibleSettings.length;
  const searchField = (
    <TextField
      type="search"
      size="small"
      fullWidth
      value={query}
      onChange={(e) => setQuery(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Escape') setQuery(''); }}
      placeholder={t('settings_search_placeholder', { defaultValue: 'Search settings' })}
      inputProps={{ 'aria-label': t('settings_search_placeholder', { defaultValue: 'Search settings' }) }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchRoundedIcon sx={{ color: c.TEXT_3 }} />
          </InputAdornment>
        ),
        endAdornment: query ? (
          <InputAdornment position="end">
            <IconButton size="small" edge="end" onClick={() => setQuery('')} aria-label={t('settings_search_clear', { defaultValue: 'Clear search' })}>
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
      sx={{ '& input[type=search]::-webkit-search-cancel-button': { display: 'none' } }}
    />
  );

  return (
    <Box
      dir={isRTL ? 'rtl' : undefined}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '260px minmax(0, 1fr)' },
        gap: 2,
        alignItems: 'start',
        minWidth: 0,
      }}
    >
      <GlobalStyles styles={PAGE_SCROLL_PADDING} />
      <Paper
        component="nav"
        aria-label={t('settings_sections_nav', { defaultValue: 'Settings sections' })}
        sx={{ p: 1.5, minWidth: 0, position: { md: 'sticky' }, top: { md: layout.APP_BAR_HEIGHT + 16 } }}
      >
        {searchField}
        {/* Computer: the section list with counts. */}
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, mt: 1, display: { xs: 'none', md: 'flex' }, flexDirection: 'column', gap: 0.5 }}>
          {SECTIONS.map((sec) => {
            const active = !searching && sec.id === activeSection.id;
            const count = countBySection[sec.id];
            return (
              <Box component="li" key={sec.id}>
                <ButtonBase
                  onClick={() => openSection(sec.id)}
                  aria-current={active ? 'true' : undefined}
                  sx={{
                    width: '100%',
                    minHeight: 44,
                    px: 1.25,
                    py: 0.75,
                    gap: 1.25,
                    borderRadius: '10px',
                    justifyContent: 'flex-start',
                    textAlign: 'start',
                    bgcolor: active ? c.ACCENT_TINT : 'transparent',
                    boxShadow: active ? `inset 3px 0 0 ${c.ACCENT}` : 'none',
                    opacity: searching && count === 0 ? 0.5 : 1,
                    '&:hover': { bgcolor: active ? c.ACCENT_TINT : c.SURFACE_ALT },
                    '&.Mui-focusVisible': { outline: `2px solid ${c.ACCENT}`, outlineOffset: 1 },
                  }}
                >
                  <IconCircle Icon={sec.Icon} hue={sec.hue} />
                  <Box component="span" sx={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, lineHeight: 1.4, color: active ? c.ACCENT_TEXT : c.INK, overflowWrap: 'anywhere' }}>
                    {tx(sec.title)}
                  </Box>
                  {changedSections[sec.id] ? <UnsavedDot /> : null}
                  <Box component="span" sx={{ fontSize: 12, fontWeight: 600, color: c.INK_2, fontVariantNumeric: 'tabular-nums' }}>
                    {count}
                  </Box>
                </ButtonBase>
              </Box>
            );
          })}
        </Box>
        {/* Phone and tablet: the same sections as a row of chips. */}
        <Box ref={chipRowRef} sx={{ display: { xs: 'flex', md: 'none' }, gap: 1, mt: 1, overflowX: 'auto', pb: 0.5, scrollbarWidth: 'thin' }}>
          {SECTIONS.map((sec) => {
            const active = !searching && sec.id === activeSection.id;
            return (
              <Chip
                key={sec.id}
                icon={<sec.Icon />}
                label={`${tx(sec.title)} · ${countBySection[sec.id]}`}
                onClick={() => openSection(sec.id)}
                variant={active ? 'filled' : 'outlined'}
                aria-current={active ? 'true' : undefined}
                data-active={active ? 'true' : undefined}
                sx={{
                  flex: 'none',
                  height: 40,
                  borderRadius: '20px',
                  fontWeight: 600,
                  ...(active
                    ? { bgcolor: c.ACCENT_TINT, color: c.ACCENT_TEXT, border: `1px solid ${c.ACCENT}`, '& .MuiChip-icon': { color: c.ACCENT_TEXT } }
                    : { color: c.INK, borderColor: c.OUTLINE_STRONG, '& .MuiChip-icon': { color: c.INK_2 } }),
                }}
              />
            );
          })}
        </Box>
        <Typography sx={{ display: { xs: 'none', md: 'block' }, mt: 1.5, pt: 1.5, borderTop: `1px solid ${c.OUTLINE}`, fontSize: 13, lineHeight: 1.5, color: c.TEXT_3 }}>
          {t('settings_footer_note', { n: totalCount, sections: SECTIONS.length, defaultValue: '{{n}} settings in {{sections}} sections. The search looks through every section.' })}
        </Typography>
      </Paper>

      <Paper component="section" aria-labelledby="gs-pane-title" sx={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ px: { xs: 2, md: 3 }, pt: 2, pb: 1.5, borderBottom: `1px solid ${c.OUTLINE}`, display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 1.5 }}>
          <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
            <Typography id="gs-pane-title" component="h2" sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.4, overflowWrap: 'anywhere' }}>
              {searching ? t('settings_search_results', { defaultValue: 'Search results' }) : tx(activeSection.title)}
            </Typography>
            <Typography sx={{ fontSize: 14, lineHeight: 1.5, color: c.TEXT_3, overflowWrap: 'anywhere' }}>
              {searching
                ? t('settings_search_summary', { query: query.trim(), n: matches.length, defaultValue: 'Matches for “{{query}}”: {{n}}' })
                : tx(activeSection.sub)}
            </Typography>
          </Box>
          {!searching && activeSection.groups ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {activeSection.groups.map((g) => (
                <Button
                  key={g.id}
                  size="small"
                  variant="text"
                  color="neutral"
                  onClick={() => {
                    const el = document.getElementById(`gs-group-${g.id}`);
                    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'start', behavior: 'smooth' });
                  }}
                >
                  {tx(g.jump)}
                </Button>
              ))}
            </Box>
          ) : null}
        </Box>

        {settingsdata.error && settingsdata.error.flag && clicked ? (
          <InlineMessage kind="error" onClose={closeUpdateError} sx={{ mx: { xs: 2, md: 3 }, mt: 2 }}>
            {t('update_failed', { defaultValue: 'Update failed' })}
          </InlineMessage>
        ) : null}

        <Box sx={{ px: { xs: 2, md: 3 }, pb: 1.5 }}>
          {paneBody}
          {!searching && activeSection.id === 'drivers' ? (
            // The onboarding wizard (master switch, video, TIN policy and the
            // per-step table) lives on its own tab; its keys ride along in
            // this form's payload untouched.
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 1.75, color: c.INK_2, fontSize: 13.5, lineHeight: 1.5 }}>
              <InfoOutlinedIcon aria-hidden="true" sx={{ fontSize: 18, mt: '1px', color: c.TEXT_3 }} />
              <span>{t('onboarding_wizard_moved', { defaultValue: 'Onboarding wizard settings (steps, TIN policy, video) are on the "Onboarding wizard" tab.' })}</span>
            </Box>
          ) : null}
          {!searching && activeSection.id === 'bookings' ? (
            // Product-variant hooks from sharedFunctions (render nothing in
            // this build): booking images, delivery flow, customer bids.
            <>
              <BookingImageSettings data={data} handleSwitchChange={handleSwitchChange} />
              <DeliveryFlow data={data.bookingFlow} handleChange={handleBookingFlowChange} />
              <CustomerBidSettings
                data={data}
                classes={{}}
                handleSwitchChange={handleSwitchChange}
                handleChangeBidPriceType={handleChangeBidPriceType}
                handleCustomerPrice={handleCustomerPrice}
              />
            </>
          ) : null}
        </Box>

        {/* The save bar stays at the bottom of the screen while the pane scrolls. */}
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: 2,
            mt: 'auto',
            bgcolor: c.WHITE,
            borderTop: `1px solid ${c.OUTLINE}`,
            borderBottomLeftRadius: `${layout.RADIUS_CARD}px`,
            borderBottomRightRadius: `${layout.RADIUS_CARD}px`,
            boxShadow: hasChanges ? '0 -6px 16px rgba(15,23,42,0.06)' : 'none',
            px: { xs: 2, md: 3 },
            pt: 1.5,
            pb: 'calc(12px + env(safe-area-inset-bottom))',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            columnGap: 1.5,
            rowGap: 1,
          }}
        >
          <Box aria-live="polite" sx={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 2, rowGap: 0.5 }}>
            {hasChanges ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 14, fontWeight: 600, color: c.INK }}>
                <UnsavedDot />
                {changedCount === 1
                  ? t('settings_unsaved_one', { defaultValue: '1 unsaved change' })
                  : t('settings_unsaved_many', { n: changedCount, defaultValue: '{{n}} unsaved changes' })}
              </Box>
            ) : (
              <Box sx={{ fontSize: 14, color: c.TEXT_3 }}>
                {t('settings_all_saved', { defaultValue: 'No unsaved changes' })}
              </Box>
            )}
            {errorKeys.length > 0 ? (
              <ButtonBase
                onClick={() => jumpTo(errorKeys[0])}
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 14, fontWeight: 600, color: c.DANGER_TEXT, borderRadius: '6px', px: 0.5, mx: -0.5 }}
              >
                <WarningAmberRoundedIcon aria-hidden="true" sx={{ fontSize: 18 }} />
                {t('settings_to_fix', { n: errorKeys.length, defaultValue: '{{n}} to fix' })}
              </ButtonBase>
            ) : null}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
            <Button variant="text" color="neutral" onClick={discard} disabled={!hasChanges || saving}>
              {t('settings_discard', { defaultValue: 'Discard' })}
            </Button>
            <Button variant="contained" startIcon={<CheckRoundedIcon />} onClick={save} disabled={!hasChanges || saving}>
              {saving ? t('settings_saving', { defaultValue: 'Saving…' }) : t('settings_save_changes', { defaultValue: 'Save changes' })}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default GeneralSettings;
